/* global globalThis */
import React, { useEffect, useState } from "react";
import "./Dashboard.css";
import { showError } from "../../../common/Utils";
import axiosClient from "../../../../api-client/AxiosClient";
import { toast } from "react-toastify";
import StatusCard from "./DashboardComponents/StatusCard.js";
import StatusTable from "./DashboardComponents/StatusTable.js";
import MappingWarningsTable from "./DashboardComponents/MappingWarningsTable.js";
import { RotateCcw } from "lucide-react";

// Extract initial data fetch logic
const fetchAllMappingFiles = async (configId) => {
  try {
    const filesResponse = await axiosClient.get(
      "api/Object/GetObjectByOrgConfigId",
      {
        params: { organizationConfigId: configId },
      },
    );
    return filesResponse.data;
  } catch (error) {
    console.error("Error fetching mapping files:", error);
    toast.error("Failed to fetch mapping files");
    return [];
  }
};

// Extract status data fetching
const fetchStatusData = async (selectedConfigId, setStatusData) => {
  if (selectedConfigId) {
    try {
      const response = await axiosClient.get(
        `/api/DashboardReport/GetMappingFilesReportByUser?organizationConfigId=${selectedConfigId}`,
      );
      setStatusData(response.data);
    } catch (error) {
      showError(error);
    }
  }
};

// Extract objects in scope fetching
const fetchObjectsInScope = async (selectedConfigId, setStatusTableData) => {
  if (selectedConfigId) {
    try {
      const response = await axiosClient.get(
        `/api/DashboardReport/GetScopedReportByUser?organizationConfigId=${selectedConfigId}`,
      );
      setStatusTableData(response.data);
    } catch (error) {
      showError(error);
    }
  }
};

// Extract mapping warnings fetching
const fetchMappingWarnings = async (
  selectedConfigId,
  setFilteredWarningData,
) => {
  if (!selectedConfigId) {
    setFilteredWarningData([]);
    return;
  }
  try {
    const response = await axiosClient.get(
      `/api/DashboardReport/getvalidationwarnings?organizationConfigId=${selectedConfigId}`,
    );
    setFilteredWarningData(response.data);
  } catch (error) {
    setFilteredWarningData([]);
    showError(error);
  }
};

const Dashboard = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [filteredMappingFiles, setFilteredMappingFiles] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [statusData, setStatusData] = useState({});
  const [statusTableData, setStatusTableData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState("All Statuses");
  const [filteredWarningData, setFilteredWarningData] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const cardItems = [
    "totalobjects",
    "objectsnotstarted",
    "inprogresscount",
    "completecount",
  ];

  const cardStatuses = ["All Statuses", "notstarted", "inprogress", "complete"];

  useEffect(() => {
    setLoggedInUser(
      JSON.parse(
        sessionStorage.getItem(
          JSON.parse(sessionStorage.getItem("msal.account.keys"))[0],
        ),
      ).name.split(" "),
    );
  }, []);

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);

        // Get configuration ID from localStorage
        const configId = localStorage.getItem("organization_config_id");

        // Get corporation for display purposes

        if (!configId) {
          setIsLoading(false);
          return;
        }

        setSelectedConfigId(configId);

        const mappingFiles = await fetchAllMappingFiles(configId);
        setFilteredMappingFiles(mappingFiles);
        setSelectedFiles(mappingFiles);
        setStatus("All Statuses");
      } catch (error) {
        showError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();

    // Listen for both localStorage events
    globalThis.addEventListener("localStorageUpdated", fetchInitialData);
    globalThis.addEventListener("customStorageChange", (e) => {
      if (e.detail?.key === "organization_config_id") {
        fetchInitialData();
      }
    });

    return () => {
      globalThis.removeEventListener("localStorageUpdated", fetchInitialData);
      globalThis.removeEventListener("customStorageChange", fetchInitialData);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".custom-dropdown-container")) {
        setDropdownOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle multi-select with checkboxes
  const handleChange = (selectedOption) => {
    if (!selectedOption) return;

    const isAlreadySelected = selectedFiles.some(
      (file) => file.objectid === selectedOption.objectid,
    );

    if (isAlreadySelected) {
      const updatedFiles = selectedFiles.filter(
        (file) => file.objectid !== selectedOption.objectid,
      );
      setSelectedFiles(updatedFiles);
    } else {
      const updatedFiles = [...selectedFiles, selectedOption];
      setSelectedFiles(updatedFiles);
    }
  };

  useEffect(() => {
    fetchStatusData(selectedConfigId, setStatusData);
    fetchObjectsInScope(selectedConfigId, setStatusTableData);
    fetchMappingWarnings(selectedConfigId, setFilteredWarningData);
  }, [selectedConfigId]);

  const cardDisplayMap = {
    totalobjects: "Total Objects in Scope",
    inprogresscount: "Objects in Progress",
    completecount: "Objects Completed",
    objectsnotstarted: "Objects Not Started",
  };

  const handleClearFilters = () => {
    setStatus("All Statuses");
    setSelectedFiles([...filteredMappingFiles]);
  };

  const filteredOptions = filteredMappingFiles.filter((option) =>
    option.description.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const allSelected = selectedFiles.length === filteredMappingFiles.length;

  // Helper function to get dropdown display text
  const getDropdownDisplayText = () => {
    if (!selectedConfigId) {
      return "Select configuration first";
    }
    if (allSelected) {
      return "All objects";
    }
    if (selectedFiles.length > 0) {
      return `${selectedFiles.length} selected`;
    }
    return "Select objects";
  };

  return (
    <div className="dashboard-container">
      {isLoading ? (
        <div className="flex flex-col justify-center items-center h-96">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-900 rounded-full animate-spin"></div>
          </div>
          <p className="mt-3 text-sm text-gray-600">
            Loading dashboard data...
          </p>
        </div>
      ) : (
        <>
          <div className="bg-gradient-to-r from-blue-800 via-blue-500 to-orange-500 rounded-3xl p-10 text-white max-w-full mb-4">
            <h1 className="text-4xl font-bold mb-4 w-full">
              Welcome to Falcon Mapping Workspace, <br />
              {loggedInUser[0]}
              {loggedInUser.length > 1 && " " + loggedInUser.at(-1)}!
            </h1>
            <p className="text-lg">
              BlueGecko Falcon Mapping is a comprehensive migration data mapping
              solution.
              <br />
              Track and manage your source-to-target mapping progress across all
              objects and modules in real-time.
            </p>
          </div>
          <div className="tab-content">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-3xl font-semibold text-blue-900 mt-2">
                Mapping Progress Overview
              </h2>
              <div className="flex flex-wrap gap-4 items-center">
                <div
                  className="custom-dropdown-container"
                  style={{ position: "relative", width: "250px" }}
                >
                  <label
                    htmlFor="objects-dropdown"
                    className="text-sm font-medium text-gray-700 mb-1"
                  >
                    Objects/Modules
                  </label>
                  <div
                    id="objects-dropdown"
                    role="combobox"
                    aria-expanded={dropdownOpen}
                    aria-haspopup="listbox"
                    className="custom-dropdown-trigger"
                    onClick={() =>
                      selectedConfigId && setDropdownOpen(!dropdownOpen)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectedConfigId && setDropdownOpen(!dropdownOpen);
                      }
                    }}
                    tabIndex={selectedConfigId ? 0 : -1}
                    style={{
                      minHeight: "40px",
                      height: "40px",
                      border: "1px solid #d1d5db",
                      borderRadius: "0.375rem",
                      padding: "0 12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: selectedConfigId ? "pointer" : "not-allowed",
                      backgroundColor: selectedConfigId ? "white" : "#f5f5f5",
                      boxShadow: dropdownOpen
                        ? "0 0 0 2px rgba(59, 130, 246, 0.5)"
                        : "none",
                      fontSize: "14px",
                      color: selectedConfigId ? "#374151" : "#9ca3af",
                      transition:
                        "border-color 0.2s ease, box-shadow 0.2s ease",
                      borderColor: dropdownOpen ? "#2563eb" : "#d1d5db",
                    }}
                    onMouseEnter={(e) => {
                      if (selectedConfigId && !dropdownOpen) {
                        e.currentTarget.style.borderColor = "#6b7280";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!dropdownOpen) {
                        e.currentTarget.style.borderColor = "#d1d5db";
                      }
                    }}
                  >
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        flex: 1,
                        margin: "0px",
                      }}
                    >
                      {getDropdownDisplayText()}
                    </div>
                    <div
                      style={{
                        width: "1px",
                        height: "20px",
                        backgroundColor: "#d1d5db",
                      }}
                    ></div>
                    <div
                      style={{
                        height: "40px",
                        padding: "8px",
                        color: "#6b7280",
                        display: "flex",
                        alignItems: "center",
                        marginLeft: "8px",
                      }}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {dropdownOpen && selectedConfigId && (
                    <div className="custom-dropdown-menu">
                      <div
                        style={{
                          padding: "8px 12px",
                          borderBottom: "1px solid #e0e0e0",
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Search objects..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div
                        style={{
                          padding: "8px 12px",
                          borderBottom: "1px solid #e0e0e0",
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: "52px",
                          zIndex: 1,
                        }}
                      >
                        {filteredOptions.length > 0 && (
                          <>
                            {allSelected ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedFiles([]);
                                }}
                                className="bg-transparent border-none cursor-pointer text-red-600 text-sm font-medium p-0"
                              >
                                Clear
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newSelections = [...selectedFiles];
                                  filteredOptions.forEach((option) => {
                                    const alreadySelected = newSelections.some(
                                      (file) =>
                                        file.objectid === option.objectid,
                                    );
                                    if (!alreadySelected) {
                                      newSelections.push(option);
                                    }
                                  });
                                  setSelectedFiles(newSelections);
                                }}
                                className="bg-transparent border-none cursor-pointer text-blue-600 text-sm font-medium p-0"
                              >
                                Select All ({filteredOptions.length})
                              </button>
                            )}
                          </>
                        )}
                      </div>

                      {filteredOptions.length > 0 ? (
                        filteredOptions.map((option) => {
                          const isSelected = selectedFiles.some(
                            (file) => file.objectid === option.objectid,
                          );
                          return (
                            <div
                              key={option.objectid}
                              role="option"
                              aria-selected={isSelected}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChange(option);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleChange(option);
                                }
                              }}
                              tabIndex={0}
                              className="flex items-center justify-between w-full px-3 py-2 cursor-pointer text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f0f0f0";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                              }}
                              title={option.description}
                            >
                              <span className="overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                                {option.description}
                              </span>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                style={{
                                  marginLeft: "10px",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChange(option);
                                }}
                                aria-label={`Select ${option.description}`}
                              />
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">
                          No objects found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-end gap-2 mt-4 md:mt-6">
                  <button
                    onClick={handleClearFilters}
                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded flex items-center"
                  >
                    <RotateCcw className="w-4 h-4 mr-2 mt-0.5" />
                    <span>Clear Filters</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="dashboard-cards">
              <div className="flex gap-4 flex-wrap justify-between">
                {cardItems.map((item, index) => (
                  <StatusCard
                    key={item}
                    index={index}
                    totalObjects={statusData["totalobjects"] || 0}
                    title={cardDisplayMap[item]}
                    count={statusData[item] || 0}
                    cardStatus={cardStatuses[index]}
                    status={status}
                    handleCardClick={(selectedStatus) =>
                      setStatus(selectedStatus)
                    }
                  />
                ))}
              </div>

              <StatusTable
                data={statusTableData}
                status={status}
                selectedObjects={selectedFiles}
                filteredMappingFiles={filteredMappingFiles}
              />
              <MappingWarningsTable
                data={filteredWarningData}
                selectedObjects={selectedFiles}
                filteredMappingFiles={filteredMappingFiles}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
